import { Grid } from './grid.ts';
import { Rule, parseRule } from './rules.ts';

export class Simulator {
  private current: Grid;
  private next: Grid;
  private _rule: Rule;
  private _generation = 0;
  private _playing = false;
  private _speed = 10;
  private timerId: number | null = null;

  onTick: (() => void) | null = null;

  constructor(width: number, height: number, ruleString: string) {
    this.current = new Grid(width, height);
    this.next = new Grid(width, height);
    this._rule = parseRule(ruleString) ?? { birth: new Set([3]), survival: new Set([2, 3]) };
  }

  get grid(): Grid {
    return this.current;
  }

  get generation(): number {
    return this._generation;
  }

  get playing(): boolean {
    return this._playing;
  }

  get speed(): number {
    return this._speed;
  }

  get rule(): Rule {
    return this._rule;
  }

  setRule(ruleString: string): boolean {
    const parsed = parseRule(ruleString);
    if (!parsed) return false;
    this._rule = parsed;
    return true;
  }

  step(): void {
    const w = this.current.width;
    const h = this.current.height;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const neighbors = this.current.countNeighbors(x, y);
        const alive = this.current.get(x, y);

        if (alive) {
          this.next.set(x, y, this._rule.survival.has(neighbors));
        } else {
          this.next.set(x, y, this._rule.birth.has(neighbors));
        }
      }
    }

    const temp = this.current;
    this.current = this.next;
    this.next = temp;
    this._generation++;
  }

  play(): void {
    if (this._playing) return;
    this._playing = true;
    this.scheduleStep();
  }

  pause(): void {
    this._playing = false;
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  togglePlay(): void {
    if (this._playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  setSpeed(generationsPerSecond: number): void {
    this._speed = Math.max(1, Math.min(120, generationsPerSecond));
    if (this._playing) {
      if (this.timerId !== null) {
        clearTimeout(this.timerId);
      }
      this.scheduleStep();
    }
  }

  reset(): void {
    this.pause();
    this.current.clear();
    this.next.clear();
    this._generation = 0;
  }

  randomize(density: number): void {
    this.current.randomize(density);
    this._generation = 0;
  }

  resize(width: number, height: number): void {
    this.current.resize(width, height);
    this.next = new Grid(width, height);
  }

  private scheduleStep(): void {
    const interval = 1000 / this._speed;
    this.timerId = window.setTimeout(() => {
      this.step();
      this.onTick?.();
      if (this._playing) {
        this.scheduleStep();
      }
    }, interval);
  }
}
